import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BasicTable from '../../components/BasicTable';
import { DatefilterFn, convertToLocalDate } from '../../utils/Utils';
import axiosInstance from '../../api/axiosInstance';
import { ErrorHandler } from '../../utils/errorHandlers';
import PopupBanner from '../../components/PopupBanner';
import BannerConfiguration from './BannerConfiguration';
import PopupModal from '../../components/PopupModal';
import { useTranslation } from 'react-i18next';

const ActionMenu = ({ item, setEditingBannerId, setDeletingBannerId }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="relative" onBlur={() => setTimeout(() => setIsOpen(false), 200)}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`p-1 rounded-full transition-colors focus:outline-none ${isOpen ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" />
                </svg>
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-hidden">
                    <button
                        onClick={() => setEditingBannerId(item?.bannerId)}
                        className="flex w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 text-left"
                    >
                        Edit
                    </button>
                    <button
                        onClick={() => {
                            setDeletingBannerId(item?.bannerId)
                        }}
                        className="flex w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
                    >
                        Delete
                    </button>
                </div>
            )}
        </div>
    );
};

const Banner = () => {
    const navigate = useNavigate();
    const [banners, setBanners] = useState([]);
    const [messageForBanner, setMessageForBanner] = useState('')
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [editingBannerId, setEditingBannerId] = useState(null)
    const [deletingBannerId, setDeletingBannerId] = useState(null);
    const { t } = useTranslation();
    const { Doyouwanttodelete, cancel, deleteText } = t('PopUpMessage');
    const [loading, setLoading] = useState(false);

    const column = useMemo(() => [
        { header: 'TITLE', accessorKey: 'title' },
        { header: 'MESSAGE', accessorKey: 'message' },
        { header: 'Severity Level', accessorKey: 'severityLevel' },
        {
            header: 'START DATE',
            accessorKey: 'startDatetime',
            cell: (info) =>
                info?.getValue() !== ' ' ? convertToLocalDate(info?.getValue()) : '',
            filterFn: DatefilterFn
        },
        {
            header: 'END DATE',
            accessorKey: 'endDatetime',
            cell: (info) =>
                info?.getValue() !== ' ' ? convertToLocalDate(info?.getValue()) : '',
            filterFn: DatefilterFn
        },
        {
            header: 'Status',
            accessorFn: (row) => row.isEnabled ? 'Active' : 'Inactive',
            id: 'isEnabled',
            filterSelectOptions: [
                { label: 'Active', value: 'Active' },
                { label: 'Inactive', value: 'Inactive' }
            ],
            cell: (info) => {
                const statusText = info.getValue();
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusText === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {statusText}
                    </span>
                );
            }
        },
        {
            header: 'ACTION',
            id: 'actions',
            cell: ({ row }) => <ActionMenu item={row.original} setEditingBannerId={setEditingBannerId} setDeletingBannerId={setDeletingBannerId} />
        }
    ], []);

    const getAllBanners = async () => {
        try {
            const res = await axiosInstance.get('/api/admin/getAllBanner');
            setBanners(res?.data?.data || []);

        } catch (error) {
            console.error("Failed to fetch banners:", error.message);
            const message = ErrorHandler(error);
            setMessageForBanner(
                <>
                    <span style={{ color: 'red', fontWeight: 'bold' }}>{message}</span>
                </>
            )

            setBanners([]);
        }
    };
    useEffect(() => {
        getAllBanners()
    }, []);

    const closeAlertModel = () => {
        setMessageForBanner('');
    }
    const togglePanel = () => {
        setIsPanelOpen(false)
        setEditingBannerId(null)
    }
    const closeConfirmModal = () => {
        setDeletingBannerId(null)
    }
    const handleConfirmDelete = () => {
        setLoading(true)
        const payload = {
            isEnabled: false,
            isDeleted: true
        }
        axiosInstance.put(`/api/admin/deleteBanner/${deletingBannerId}`, payload)
            .then((res) => {
                console.log(res, "res")
                setMessageForBanner(res?.data?.message || "Operation successful!");
                setDeletingBannerId(null)
                getAllBanners()
                setLoading(false)
            })
            .catch((error) => {
                const message = ErrorHandler(error);
                setLoading(false)
                setMessageForBanner(
                    <>
                        <span style={{ color: 'red', fontWeight: 'bold' }}>{message}</span>
                    </>
                )
            })

    }

    const isConfigActive = isPanelOpen || editingBannerId !== null;
    return (
        <>
            {loading ?
                <div className='flex justify-center items-center h-[50vh]'>
                    <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500'></div>
                </div> :
                <div className='p-2 w-full flex flex-col gap-5'>
                    <div className='w-full flex flex-col gap-1'>
                        <p className='text-xl font-medium'>Banner Management</p>
                        <div className='w-full text-xs flex justify-start items-center gap-1 font-medium text-gray-600'>
                            <span className='hover:underline cursor-pointer' onClick={() => navigate('/admin')}>Home</span>
                            <span>{'>'}</span>
                            <span>Banners</span>
                        </div>
                    </div>
                    <div className='w-full'>
                        <BasicTable columns={column} tableData={banners} colheight={"8"} setIsPanelOpen={setIsPanelOpen} />
                    </div>
                </div>
            }
            {isConfigActive &&
                <BannerConfiguration
                    isPanelOpen={isConfigActive}
                    togglePanel={togglePanel}
                    editingBannerId={editingBannerId}
                    getAllBanners={getAllBanners}
                    setMessageForBanner={setMessageForBanner} />
            }
            {messageForBanner &&
                <PopupBanner
                    isOpen={messageForBanner}
                    message={messageForBanner}
                    onCancel={closeAlertModel}
                />
            }
            {deletingBannerId &&
                <PopupModal
                    isOpen={deletingBannerId}
                    message={Doyouwanttodelete}
                    onCancel={closeConfirmModal}
                    cancelText={cancel}
                    onConfirm={handleConfirmDelete}
                    confirmText={deleteText}
                />
            }

        </>
    );
};

export default Banner;